<template>
  <NavBar pageName="Clients"></NavBar>

  <!-- Search Filters Card -->
  <v-container class="py-2" style="margin-top: 185px;">
    <v-card class="mb-4">
      <v-card-title>{{ t.clients.search_clients }}</v-card-title>
      <v-card-text>
        <!-- Client Filter Component -->
        <ClientSearchFilter
          ref="clientSearchFilter"
          v-model="filters.parentClientId"
          v-model:include-subclients="filters.includeSubclients"
          :client-select-label="t.clients.filter_by_parent"
          @hierarchy-loaded="onHierarchyLoaded"
        />

        <!-- Search Field -->
        <v-row class="mt-2">
          <v-col cols="12" md="8">
            <v-text-field
                v-model="filters.searchTerm"
                :label="t.clients.search_by"
                :placeholder="t.clients.search_placeholder"
                clearable
                dense
                outlined
                hide-details
                @keyup.enter="loadClients"
                @click="$event.target.select()"
                @dblclick="filters.searchTerm = ''"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Deleted Filter -->
        <v-row class="mt-2">
          <v-col cols="12">
            <v-radio-group v-model="filters.showDeleted" inline hide-details @update:model-value="loadClients">
              <v-radio :label="t.clients?.all_except_deleted || 'All (except deleted)'" value="active"></v-radio>
              <v-radio :label="t.clients?.deleted_only || 'Deleted'" value="deleted"></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row class="mt-2">
          <v-col cols="12" class="d-flex justify-space-between">
            <v-btn color="primary" @click="loadClients" :loading="loading">
              {{ t.common.search }}
            </v-btn>
            <v-btn color="primary" @click="openAddNewClient">
              {{ t.clients.add_new_client }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Results Card -->
    <v-card v-if="clientsLoaded">
      <v-card-title class="d-flex align-center justify-space-between">
        <div>
          {{ t.clients.found_clients }}: {{ totalCount }}
        </div>
        <!-- Navigation breadcrumb when viewing a subclient's children -->
        <div v-if="currentParentClient" class="d-flex align-center">
          <v-btn
            size="small"
            color="secondary"
            variant="outlined"
            class="mr-2"
            @click="goBack"
          >
            <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
            Back
          </v-btn>
          <v-chip color="info" size="small">
            <v-icon size="x-small" class="mr-1">mdi-folder-account</v-icon>
            {{ currentParentClient.name }}
          </v-chip>
        </div>
      </v-card-title>

      <!-- Current Client Card -->
      <v-card v-if="currentClient" class="mx-4 mt-2 mb-3" variant="outlined" color="primary">
        <v-card-title class="bg-primary text-white py-2 d-flex align-center" style="font-size: 14px;">
          <v-icon class="mr-2" size="small">mdi-account-star</v-icon>
          Clientul curent / Current Client
        </v-card-title>
        <v-card-text class="pa-0">
          <v-table density="compact">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Short Name</th>
                <th>Type</th>
                <th>Client Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ currentClient.id }}</td>
                <td>
                  <div class="font-weight-medium text-primary d-flex align-center cursor-pointer"
                       @click="canDrillDown(currentClient) ? drillDown(currentClient) : null"
                       :title="`ID: ${currentClient.id} | Status: ${currentClient.status_name} | Type: ${currentClient.client_type_name}`">
                    <span>{{ currentClient.name }}</span>
                    <v-icon v-if="canDrillDown(currentClient)" size="small" color="primary" class="ml-1">mdi-chevron-right-circle</v-icon>
                  </div>
                  <div v-if="currentClient.parent_path" class="text-caption ancestor-path">{{ currentClient.parent_path }}</div>
                </td>
                <td>{{ currentClient.short_name }}</td>
                <td>
                  <v-icon v-if="currentClient.legal_type === 2" size="small" class="mr-1">mdi-domain</v-icon>
                  <v-icon v-else size="small" class="mr-1">mdi-account</v-icon>
                  {{ currentClient.legal_type_name }}
                </td>
                <td>{{ currentClient.client_type_name }}</td>
                <td>
                  <v-chip :color="getStatusColor(currentClient.status_name)" size="small">{{ currentClient.status_name }}</v-chip>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <v-btn size="x-small" color="success" :variant="currentClientExpanded ? 'flat' : 'elevated'" class="mr-2" @click="toggleCurrentClientExpand">
                      <v-icon size="small" class="mr-1">{{ currentClientExpanded ? 'mdi-eye' : 'mdi-eye-outline' }}</v-icon>Info
                    </v-btn>
                    <v-btn size="x-small" color="teal" class="mr-2" @click="goToClientSims(currentClient)">
                      <v-icon size="small" class="mr-1">mdi-sim</v-icon>SIMs
                    </v-btn>
                    <v-btn size="x-small" color="deep-purple" class="mr-2" @click="goToClientUsers(currentClient)">
                      <v-icon size="small" class="mr-1">mdi-account-group</v-icon>Users
                    </v-btn>
                    <v-btn v-if="isActiveClient(currentClient)" size="x-small" color="primary" class="mr-2" @click="openAddClientServiceForClient(currentClient)">
                      <v-icon size="small" class="mr-1">mdi-plus</v-icon>Add CS
                    </v-btn>
                    <v-menu>
                      <template v-slot:activator="{ props }">
                        <v-btn size="x-small" color="grey-darken-1" v-bind="props">
                          More Actions <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
                        </v-btn>
                      </template>
                      <v-list density="compact">
                        <v-list-subheader>More Actions</v-list-subheader>
                        <v-list-item :disabled="isBlockedClient(currentClient)" @click="openEditClientDialog(currentClient)">
                          <template v-slot:prepend><v-icon color="info" size="small">mdi-pencil</v-icon></template>
                          <v-list-item-title>Edit</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="canShowApproveButton(currentClient)" :disabled="!canApprove(currentClient)" @click="openApproveDialog(currentClient)">
                          <template v-slot:prepend><v-icon color="success" size="small">mdi-check-circle</v-icon></template>
                          <v-list-item-title>{{ t.clients.approve }}</v-list-item-title>
                        </v-list-item>
                        <template v-if="isActiveClient(currentClient)">
                          <v-divider></v-divider>
                          <v-list-item @click="openClientActionDialog(currentClient, 'limit')">
                            <template v-slot:prepend><v-icon color="warning" size="small">mdi-speedometer-slow</v-icon></template>
                            <v-list-item-title>{{ t.clients.limit }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="openClientActionDialog(currentClient, 'suspend')">
                            <template v-slot:prepend><v-icon color="orange" size="small">mdi-pause-circle</v-icon></template>
                            <v-list-item-title>{{ t.clients.suspend }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="openClientActionDialog(currentClient, 'delete')">
                            <template v-slot:prepend><v-icon color="error" size="small">mdi-delete</v-icon></template>
                            <v-list-item-title>{{ t.common.delete }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="openClientActionDialog(currentClient, 'block')">
                            <template v-slot:prepend><v-icon color="black" size="small">mdi-block-helper</v-icon></template>
                            <v-list-item-title>{{ t.clients.block }}</v-list-item-title>
                          </v-list-item>
                        </template>
                        <v-list-item v-if="isBlockedClient(currentClient)" @click="openClientActionDialog(currentClient, 'unblock')">
                          <template v-slot:prepend><v-icon color="success" size="small">mdi-lock-open</v-icon></template>
                          <v-list-item-title>{{ t.clients.unblock }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="isSuspendedClient(currentClient)" @click="openClientActionDialog(currentClient, 'reactivate')">
                          <template v-slot:prepend><v-icon color="success" size="small">mdi-play-circle</v-icon></template>
                          <v-list-item-title>{{ t.clients.reactivate }}</v-list-item-title>
                        </v-list-item>
                        <v-divider v-if="canImpersonate(currentClient)"></v-divider>
                        <v-list-item v-if="canImpersonate(currentClient)" @click="openImpersonationDialog(currentClient)" :disabled="loadingImpersonationUsers">
                          <template v-slot:prepend><v-icon color="warning" size="small">mdi-account-switch</v-icon></template>
                          <v-list-item-title>Login ca acest client</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
          <!-- Expanded details for current client -->
          <div v-if="currentClientExpanded" class="pa-4 bg-grey-lighten-4">
            <v-row>
              <v-col cols="12" md="6">
                <h4 class="mb-2">{{ t.common.phone }} / {{ t.common.email }}</h4>
                <div><strong>{{ t.common.phone }}:</strong> {{ currentClient.phone || '-' }}</div>
                <div><strong>{{ t.common.email }}:</strong> {{ currentClient.email || '-' }}</div>
                <div><strong>{{ t.clients.notification_email }}:</strong> {{ currentClient.notification_email || '-' }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <h4 class="mb-2">{{ t.clients.billing_type }}</h4>
                <div><strong>{{ t.clients.billing_type }}:</strong> {{ currentClient.billing_type_name }}</div>
                <div><strong>{{ t.clients.max_credit }}:</strong> {{ currentClient.max_credit }}</div>
                <div><strong>{{ t.clients.current_debit }}:</strong> {{ currentClient.current_debit }}</div>
                <div><strong>{{ t.clients.current_credit }}:</strong> {{ currentClient.current_credit }}</div>
              </v-col>
            </v-row>
            <v-row v-if="currentClient.legal_type === 2">
              <v-col cols="12">
                <h4 class="mb-2">{{ t.clients.company_details }}</h4>
                <div><strong>{{ t.clients.company_name }}:</strong> {{ currentClient.company_name || '-' }}</div>
                <div><strong>{{ t.clients.cui }}:</strong> {{ currentClient.cui || '-' }}</div>
                <div><strong>{{ t.clients.registration_number }}:</strong> {{ currentClient.registration_number || '-' }}</div>
                <div><strong>{{ t.clients.bank }}:</strong> {{ currentClient.bank || '-' }}</div>
                <div><strong>{{ t.clients.iban }}:</strong> {{ currentClient.iban || '-' }}</div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h4 class="mb-2">{{ t.common.info }}</h4>
                <div><strong>{{ t.clients.parent }}:</strong> {{ currentClient.parent_client_name || '-' }}</div>
                <div><strong>{{ t.common.created_at }}:</strong> {{ formatDate(currentClient.created_at) }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <h4 class="mb-2">{{ t.clients?.distributor_status || 'Distributor Status' }}</h4>
                <DistributorToggle
                  :client-id="currentClient.id"
                  :model-value="currentClient.is_distributor"
                  :token="token"
                  :disabled="!canManageDistributorFor(currentClient)"
                  @updated="(val) => currentClient.is_distributor = val"
                  @error="(msg) => alert(msg)"
                />
              </v-col>
            </v-row>
            <v-row class="mt-3">
              <v-col cols="12">
                <ClientServicesStats v-if="token" :client-id="currentClient.id" :token="token" :include-subclients="false" />
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <v-card-text>
        <div v-if="currentClient" class="text-subtitle-1 font-weight-bold mb-2">
          Subclienti / Subclients ({{ totalCount }})
        </div>
        <v-data-table
            :headers="tableHeaders"
            :items="clients"
            :loading="loading"
            :items-per-page="perPage"
            :sort-by="sortBy"
            item-value="id"
            v-model:expanded="expandedRows"
            @update:sort-by="onSortChange"
            class="elevation-1"
        >
          <!-- Name column with ancestor path -->
          <template #[`item.name`]="{ item }">
            <div
              :title="`ID: ${item.id} | Status: ${item.status_name} | Type: ${item.client_type_name}`"
              class="clickable-name"
              @click="handleClientNameClick(item)"
            >
              <div class="font-weight-medium text-primary d-flex align-center">
                <span>{{ item.name }}</span>
                <v-icon
                  v-if="canDrillDown(item)"
                  size="small"
                  color="primary"
                  class="ml-1"
                >mdi-chevron-right-circle</v-icon>
              </div>
              <div v-if="item.parent_path" class="text-caption ancestor-path">
                {{ item.parent_path }}
              </div>
            </div>
          </template>

          <!-- Status column with color -->
          <template #[`item.status_name`]="{ item }">
            <v-chip :color="getStatusColor(item.status_name)" size="small">
              {{ item.status_name }}
            </v-chip>
          </template>

          <!-- Legal Type column -->
          <template #[`item.legal_type_name`]="{ item }">
            <v-icon v-if="item.legal_type === 2" size="small" class="mr-1">mdi-domain</v-icon>
            <v-icon v-else size="small" class="mr-1">mdi-account</v-icon>
            {{ item.legal_type_name }}
          </template>

          <!-- Actions column -->
          <template #[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <!-- Info button - toggles expanded details -->
              <v-btn
                  size="x-small"
                  color="success"
                  :variant="isRowExpanded(item.id) ? 'flat' : 'elevated'"
                  class="mr-2"
                  @click="toggleRowExpand(item.id)"
              >
                <v-icon size="small" class="mr-1">{{ isRowExpanded(item.id) ? 'mdi-eye' : 'mdi-eye-outline' }}</v-icon>
                Info
              </v-btn>

              <!-- Show SIMs button -->
              <v-btn
                size="x-small"
                color="teal"
                class="mr-2"
                @click="goToClientSims(item)"
              >
                <v-icon size="small" class="mr-1">mdi-sim</v-icon>
                SIMs
              </v-btn>

              <!-- Show Users button -->
              <v-btn
                size="x-small"
                color="deep-purple"
                class="mr-2"
                @click="goToClientUsers(item)"
              >
                <v-icon size="small" class="mr-1">mdi-account-group</v-icon>
                Users
              </v-btn>

              <!-- Add Client Service button -->
              <v-btn
                v-if="isActiveClient(item)"
                size="x-small"
                color="primary"
                class="mr-2"
                @click="openAddClientServiceForClient(item)"
              >
                <v-icon size="small" class="mr-1">mdi-plus</v-icon>
                Add CS
              </v-btn>

              <!-- More Actions dropdown -->
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                      size="x-small"
                      color="grey-darken-1"
                      v-bind="props"
                  >
                    More Actions
                    <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-subheader>More Actions</v-list-subheader>
                  <!-- Edit -->
                  <v-list-item
                      :disabled="isBlockedClient(item)"
                      @click="openEditClientDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="info" size="small">mdi-pencil</v-icon>
                    </template>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <!-- Approve (for waiting_for_approval) -->
                  <v-list-item
                      v-if="canShowApproveButton(item)"
                      :disabled="!canApprove(item)"
                      @click="openApproveDialog(item)"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success" size="small">mdi-check-circle</v-icon>
                    </template>
                    <v-list-item-title>{{ t.clients.approve }}</v-list-item-title>
                  </v-list-item>
                  <!-- Actions for active clients -->
                  <template v-if="isActiveClient(item)">
                    <v-divider></v-divider>
                    <v-list-item @click="openClientActionDialog(item, 'limit')">
                      <template v-slot:prepend>
                        <v-icon color="warning" size="small">mdi-speedometer-slow</v-icon>
                      </template>
                      <v-list-item-title>{{ t.clients.limit }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openClientActionDialog(item, 'suspend')">
                      <template v-slot:prepend>
                        <v-icon color="orange" size="small">mdi-pause-circle</v-icon>
                      </template>
                      <v-list-item-title>{{ t.clients.suspend }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openClientActionDialog(item, 'delete')">
                      <template v-slot:prepend>
                        <v-icon color="error" size="small">mdi-delete</v-icon>
                      </template>
                      <v-list-item-title>{{ t.common.delete }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openClientActionDialog(item, 'block')">
                      <template v-slot:prepend>
                        <v-icon color="black" size="small">mdi-block-helper</v-icon>
                      </template>
                      <v-list-item-title>{{ t.clients.block }}</v-list-item-title>
                    </v-list-item>
                  </template>
                  <!-- Unblock (for blocked clients) -->
                  <v-list-item
                      v-if="isBlockedClient(item)"
                      @click="openClientActionDialog(item, 'unblock')"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success" size="small">mdi-lock-open</v-icon>
                    </template>
                    <v-list-item-title>{{ t.clients.unblock }}</v-list-item-title>
                  </v-list-item>
                  <!-- Reactivate (for suspended clients) -->
                  <v-list-item
                      v-if="isSuspendedClient(item)"
                      @click="openClientActionDialog(item, 'reactivate')"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success" size="small">mdi-play-circle</v-icon>
                    </template>
                    <v-list-item-title>{{ t.clients.reactivate }}</v-list-item-title>
                  </v-list-item>
                  <!-- Login as client (Impersonation) -->
                  <v-divider v-if="canImpersonate(item)"></v-divider>
                  <v-list-item
                      v-if="canImpersonate(item)"
                      @click="openImpersonationDialog(item)"
                      :disabled="loadingImpersonationUsers"
                  >
                    <template v-slot:prepend>
                      <v-icon color="warning" size="small">mdi-account-switch</v-icon>
                    </template>
                    <v-list-item-title>Login ca acest client</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>

          <!-- Expanded row with full details -->
          <template v-slot:expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length" class="pa-4 bg-grey-lighten-4">
                <v-row>
                  <v-col cols="12" md="6">
                    <h4 class="mb-2">{{ t.common.phone }} / {{ t.common.email }}</h4>
                    <div><strong>{{ t.common.phone }}:</strong> {{ item.phone || '-' }}</div>
                    <div><strong>{{ t.common.email }}:</strong> {{ item.email || '-' }}</div>
                    <div><strong>{{ t.clients.notification_email }}:</strong> {{ item.notification_email || '-' }}</div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <h4 class="mb-2">{{ t.clients.billing_type }}</h4>
                    <div><strong>{{ t.clients.billing_type }}:</strong> {{ item.billing_type_name }}</div>
                    <div><strong>{{ t.clients.max_credit }}:</strong> {{ item.max_credit }}</div>
                    <div><strong>{{ t.clients.current_debit }}:</strong> {{ item.current_debit }}</div>
                    <div><strong>{{ t.clients.current_credit }}:</strong> {{ item.current_credit }}</div>
                  </v-col>
                </v-row>
                <v-row v-if="item.legal_type === 2">
                  <v-col cols="12">
                    <h4 class="mb-2">{{ t.clients.company_details }}</h4>
                    <div><strong>{{ t.clients.company_name }}:</strong> {{ item.company_name || '-' }}</div>
                    <div><strong>{{ t.clients.cui }}:</strong> {{ item.cui || '-' }}</div>
                    <div><strong>{{ t.clients.registration_number }}:</strong> {{ item.registration_number || '-' }}</div>
                    <div><strong>{{ t.clients.bank }}:</strong> {{ item.bank || '-' }}</div>
                    <div><strong>{{ t.clients.iban }}:</strong> {{ item.iban || '-' }}</div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="6">
                    <h4 class="mb-2">{{ t.common.info }}</h4>
                    <div><strong>{{ t.clients.parent }}:</strong> {{ item.parent_client_name || '-' }}</div>
                    <div><strong>{{ t.common.created_at }}:</strong> {{ formatDate(item.created_at) }}</div>
                  </v-col>
                  <v-col cols="12" md="6">
                    <h4 class="mb-2">{{ t.clients?.distributor_status || 'Distributor Status' }}</h4>
                    <DistributorToggle
                      :client-id="item.id"
                      :model-value="item.is_distributor"
                      :token="token"
                      :disabled="!canManageDistributorFor(item)"
                      @updated="(val) => item.is_distributor = val"
                      @error="(msg) => alert(msg)"
                    />
                  </v-col>
                </v-row>
                <!-- Client Services Stats -->
                <v-row class="mt-3">
                  <v-col cols="12">
                    <ClientServicesStats
                      v-if="token"
                      :client-id="item.id"
                      :token="token"
                      :include-subclients="false"
                    />
                  </v-col>
                </v-row>
                <v-row class="mt-3">
                  <v-col cols="12">
                    <h4 class="mb-2">{{ t.common?.actions || 'Actions' }}</h4>
                    <v-btn
                      size="small"
                      color="success"
                      variant="outlined"
                      class="mr-2"
                      @click="navigateToClientSims(item)"
                    >
                      <v-icon class="mr-1">mdi-sim</v-icon>
                      SIM-uri
                    </v-btn>
                    <v-btn
                      size="small"
                      color="info"
                      variant="outlined"
                      @click="navigateToClientDetails(item.id)"
                    >
                      <v-icon class="mr-1">mdi-pencil</v-icon>
                      {{ t.common?.edit || 'Edit' }}
                    </v-btn>
                  </v-col>
                </v-row>
              </td>
            </tr>
          </template>

          <!-- No data -->
          <template v-slot:no-data>
            <v-alert type="info" class="ma-2">
              {{ t.messages.no_results }}
            </v-alert>
          </template>
        </v-data-table>

        <!-- Pagination -->
        <v-pagination
            v-model="page"
            :length="Math.ceil(totalCount / perPage)"
            @update:model-value="loadClients"
            class="mt-4"
            v-if="totalCount > perPage"
        ></v-pagination>
      </v-card-text>
    </v-card>
  </v-container>

  <!-- Add New Client Dialog -->
  <AddNewClient
      v-if="showAddNewClient"
      :client_id="client_id"
      @reloadClientsEvent="reloadClients"
      @dialogClosedEvent="dialogClosedMethod"
      @dialogEscapedEvent="dialogEscapedMethod"
  ></AddNewClient>

  <!-- Approve Dialog -->
  <v-dialog v-model="approveDialog" max-width="400" @keydown.enter="executeApprove">
    <v-card>
      <v-card-title>{{ t.clients.approve }}</v-card-title>
      <v-card-text>
        {{ t.messages.confirm_action }} <strong>{{ approveItem?.name }}</strong>?
        <br><br>
        <div><strong>{{ t.clients.billing_type }}:</strong> {{ approveItem?.billing_type_name }}</div>
        <div><strong>{{ t.clients.client_type }}:</strong> {{ approveItem?.client_type_name }}</div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="approveDialog = false">{{ t.common.cancel }}</v-btn>
        <v-btn color="success" @click="executeApprove" :loading="approveLoading">{{ t.clients.approve }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Client Action Dialog (Limit/Suspend/Delete) -->
  <v-dialog v-model="clientActionDialog" max-width="450">
    <v-card>
      <v-card-title :class="clientActionTitleClass">
        {{ clientActionTitle }}
      </v-card-title>
      <v-card-text class="pt-4">
        <p>{{ clientActionMessage }}</p>
        <div v-if="clientActionItem" class="mt-3 pa-3 bg-grey-lighten-4 rounded">
          <div><strong>{{ t.common.id }}:</strong> {{ clientActionItem.id }}</div>
          <div><strong>{{ t.common.name }}:</strong> {{ clientActionItem.name }}</div>
          <div><strong>{{ t.clients.short_name }}:</strong> {{ clientActionItem.short_name || '-' }}</div>
          <div><strong>{{ t.clients.client_type }}:</strong> {{ clientActionItem.client_type_name }}</div>
          <div><strong>{{ t.clients.billing_type }}:</strong> {{ clientActionItem.billing_type_name }}</div>
          <!-- Additional fields for suspend action -->
          <template v-if="clientActionType === 'suspend'">
            <v-divider class="my-2"></v-divider>
            <div><strong>{{ t.clients.current_credit }}:</strong> {{ clientActionItem.current_credit ?? 0 }}</div>
            <div><strong>{{ t.clients.current_debit }}:</strong> {{ clientActionItem.current_debit ?? 0 }}</div>
          </template>
        </div>
        <template v-if="clientActionType === 'delete'">
          <v-alert type="warning" variant="tonal" density="compact" class="mt-3">
            <strong>GDPR:</strong> Datele personale vor fi anonimizate ireversibil. Informatiile financiare si rapoartele vor fi pastrate.
          </v-alert>
          <v-textarea
            v-model="deleteReason"
            label="Motiv stergere"
            placeholder="Introduceti motivul stergerii..."
            rows="2"
            variant="outlined"
            density="compact"
            class="mt-3"
          ></v-textarea>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="clientActionDialog = false">{{ t.common.cancel }}</v-btn>
        <v-btn
            :color="clientActionButtonColor"
            @click="executeClientAction"
            :loading="clientActionLoading"
        >
          {{ clientActionButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Impersonation Dialog -->
  <v-dialog v-model="showImpersonationDialog" max-width="500">
    <v-card>
      <v-card-title class="bg-warning">
        <v-icon class="mr-2">mdi-account-switch</v-icon>
        Login ca {{ impersonationClientName }}
      </v-card-title>
      <v-card-text class="pt-4">
        <p class="mb-3">Selecteaza userul cu care doresti sa te loghezi:</p>
        <v-list v-if="impersonationUsers.length > 0" density="compact">
          <v-list-item
            v-for="user in impersonationUsers"
            :key="user.id"
            @click="impersonateUser(user)"
            :disabled="generatingToken"
            class="mb-1"
            rounded
          >
            <template v-slot:prepend>
              <v-icon color="primary">mdi-account</v-icon>
            </template>
            <v-list-item-title>{{ user.name || user.email }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ user.email }}
              <v-chip v-if="user.role_name" size="x-small" class="ml-2">{{ user.role_name }}</v-chip>
            </v-list-item-subtitle>
            <template v-slot:append>
              <v-icon color="grey">mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
        </v-list>
        <v-alert v-else type="warning" density="compact">
          Nu exista useri activi pentru acest client
        </v-alert>
        <v-progress-linear v-if="generatingToken" indeterminate color="warning" class="mt-2"></v-progress-linear>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="showImpersonationDialog = false" :disabled="generatingToken">Inchide</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Add Client Service Dialog -->
  <AddNewClientService
    :show="showAddClientServiceDialog"
    :initialClientId="addCsClientId"
    @close="showAddClientServiceDialog = false"
    @serviceAdded="onClientServiceAdded"
  />

  <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
    {{ snackbarMessage }}
    <template v-slot:actions>
      <v-btn variant="text" @click="snackbar = false">OK</v-btn>
    </template>
  </v-snackbar>
</template>


<script>
import { defineComponent } from 'vue';
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import { getClientsList, approveClient, limitClient, suspendClient, deleteClient, unblockClient, blockClient, reactivateClient } from "@/composables/useGetClientsListAPI"
import { getClientUsersForImpersonation, generateImpersonationToken, openImpersonationTab } from "@/composables/useImpersonation";
import { log } from "@/composables/useLog";
import NavBar from "@/components/NavBar.vue";
import AddNewClient from "@/components/AddNewClient.vue";
import AddNewClientService from "@/components/AddNewClientService.vue";
import ClientServicesStats from "@/components/ClientServicesStats.vue";
import ClientSearchFilter from "@/components/ClientSearchFilter.vue";
import DistributorToggle from "@/components/selects/DistributorToggle.vue";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'ClientsView',
  components: {
    AddNewClient,
    AddNewClientService,
    NavBar,
    ClientSearchFilter,
    ClientServicesStats,
    DistributorToggle
  },
  data() {
    return {
      showAddNewClient: false,
      showEditClientDialog: false,
      editClientId: null,
      // Add Client Service dialog
      showAddClientServiceDialog: false,
      addCsClientId: null,
      token: '',
      client_id: null,
      user: store.getters.getUser,

      // Search filters
      filters: {
        searchTerm: '',
        parentClientId: null,
        clientId: null,  // Direct client lookup by ID
        includeSubclients: false,  // Default: only direct clients
        showDeleted: 'active',  // 'active' = all except deleted, 'deleted' = only deleted
      },

      // Table data
      currentClient: null,
      currentClientExpanded: false,
      clients: [],
      clientsLoaded: false,
      loading: false,
      totalCount: 0,
      hasSubclients: false,
      navigationHistory: [],  // Stack of parent client IDs for back navigation
      clientsHierarchyData: [],  // Store hierarchy data for lookup
      rootClientId: null,  // The root client ID (user's own client)
      expandedRows: [],  // Track expanded rows for details view

      // Permissions
      permissions: {
        can_approve_prepaid: false,
        can_approve_postpaid: false,
      },

      // Pagination
      page: 1,
      perPage: 20,

      // Sorting
      sortBy: [{ key: 'name', order: 'asc' }],

      // Approve dialog
      approveDialog: false,
      approveLoading: false,
      approveItem: null,

      // Client action dialog (Limit/Suspend/Delete)
      clientActionDialog: false,
      clientActionLoading: false,
      clientActionItem: null,
      clientActionType: null, // 'limit', 'suspend', 'delete'
      deleteReason: '',

      // Snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // Impersonation dialog
      showImpersonationDialog: false,
      loadingImpersonationUsers: false,
      generatingToken: false,
      impersonationUsers: [],
      impersonationClientName: '',
      impersonationItem: null,

      // Table headers
      tableHeaders: [
        { title: 'ID', key: 'id', sortable: true },
        { title: 'Name', key: 'name', sortable: true },
        { title: 'Short Name', key: 'short_name', sortable: true },
        { title: 'Type', key: 'legal_type_name', sortable: false },
        { title: 'Client Type', key: 'client_type_name', sortable: true },
        { title: 'Status', key: 'status_name', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false },
      ],
    }
  },
  methods: {
    async loadClients() {
      this.loading = true;
      try {
        const options = {
          search_term: this.filters.searchTerm ? this.filters.searchTerm.trim() : '',
          parent_client_id: this.filters.parentClientId,
          client_id: this.filters.clientId,
          include_subclients: this.filters.includeSubclients,
          show_deleted: this.filters.showDeleted,
          sort_by: this.sortBy[0]?.key || 'name',
          sort_order: this.sortBy[0]?.order || 'asc',
          page: this.page,
          per_page: this.perPage
        };

        const response = await getClientsList(this.token, options);

        if (response.error) {
          log("Error loading clients:", response.message);
        } else {
          this.clients = response.clients;
          this.currentClient = response.current_client || null;
          this.totalCount = response.total_count;
          this.hasSubclients = response.filters?.has_subclients || false;
          this.permissions = response.permissions || { can_approve_prepaid: false, can_approve_postpaid: false };
          this.clientsLoaded = true;
        }
      } catch (err) {
        console.error("Error loading clients:", err);
      } finally {
        this.loading = false;
      }
    },

    navigateToClientDetails(clientId) {
      // Navigate to EditClientView page
      this.$router.push({ name: 'editClient', query: { id: clientId } });
    },

    // Toggle row expansion for details view
    toggleRowExpand(clientId) {
      const index = this.expandedRows.indexOf(clientId);
      if (index > -1) {
        this.expandedRows.splice(index, 1);
      } else {
        this.expandedRows.push(clientId);
      }
    },

    // Check if a row is expanded
    isRowExpanded(clientId) {
      return this.expandedRows.includes(clientId);
    },

    // Navigate to SIMs list for a client (excluding terminated, without subclients for performance)
    navigateToClientSims(client) {
      this.$router.push({
        path: '/sims',
        query: {
          client_id: client.id,
          status: 'all_except_terminated',
          include_subclients: 'false'
        }
      });
    },

    onSortChange(sortBy) {
      this.sortBy = sortBy;
      this.loadClients();
    },

    getStatusColor(statusName) {
      const status = statusName?.toLowerCase();
      if (status === 'active') return 'success';
      if (status === 'inactive' || status === 'suspended') return 'warning';
      if (status === 'waiting_for_approval') return 'orange';
      if (status === 'deleted' || status === 'terminated') return 'error';
      return 'grey';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('ro-RO');
    },

    // Check if approve button should be shown (only for waiting_for_approval status)
    canShowApproveButton(item) {
      return item.status_name?.toLowerCase() === 'waiting_for_approval';
    },

    // Check if user can approve this specific client (based on billing type and permissions)
    canApprove(item) {
      const billingType = item.billing_type_name?.toLowerCase();
      if (billingType === 'prepaid') {
        return this.permissions.can_approve_prepaid;
      } else if (billingType === 'postpaid') {
        return this.permissions.can_approve_postpaid;
      }
      return false;
    },

    openApproveDialog(item) {
      this.approveItem = item;
      this.approveDialog = true;
    },

    async executeApprove() {
      if (!this.approveItem) return;

      this.approveLoading = true;
      try {
        const response = await approveClient(this.token, this.approveItem.id);

        if (response.error) {
          alert(response.message);
        } else {
          alert(response.message);
          this.approveDialog = false;
          this.loadClients();
        }
      } catch (err) {
        console.error("Error approving client:", err);
        alert("Error approving client");
      } finally {
        this.approveLoading = false;
      }
    },

    dialogClosedMethod() {
      this.showAddNewClient = false;
      log("Dialog closed");
    },

    dialogEscapedMethod() {
      this.showAddNewClient = false;
      log("Dialog escaped");
    },

    toggleCurrentClientExpand() {
      this.currentClientExpanded = !this.currentClientExpanded;
    },

    reloadClients() {
      this.showAddNewClient = false;
      this.loadClients();
    },

    openAddNewClient() {
      this.showAddNewClient = true;
    },

    goToClientSims(item) {
      this.$router.push({ path: '/sims', query: { client_id: item.id, include_subclients: 'false' } });
    },

    goToClientUsers(item) {
      this.$router.push({ path: '/users', query: { client_id: item.id } });
    },

    openAddClientServiceForClient(item) {
      this.addCsClientId = item.id;
      this.showAddClientServiceDialog = true;
    },

    onClientServiceAdded() {
      this.showAddClientServiceDialog = false;
      this.addCsClientId = null;
    },

    openEditClientDialog(client) {
      // Navigate to EditClientView page
      this.$router.push({ name: 'editClient', query: { id: client.id } });
    },

    onClientSaved() {
      this.loadClients();
    },

    onHierarchyLoaded({ hasSubclients, hierarchy, currentClientId }) {
      this.hasSubclients = hasSubclients;
      this.clientsHierarchyData = hierarchy || [];
      this.rootClientId = currentClientId;
    },

    // Check if client can be drilled down (has subclients or is distributor)
    canDrillDown(item) {
      // Check if client has subclients or is a distributor (can have subclients)
      return item.has_subclients || item.is_distributor;
    },

    // Handle click on client name - drill down for MultiLevel/Unilevel, otherwise go to details
    handleClientNameClick(item) {
      if (this.canDrillDown(item)) {
        // Save current parent to navigation history for back navigation
        this.navigationHistory.push(this.filters.parentClientId);
        // Drill down: set this client as parent and load their direct subclients
        this.filters.parentClientId = item.id;
        this.filters.clientId = null;  // Clear direct lookup so parent_client_id is used
        this.filters.includeSubclients = false;
        // Note: The hierarchy already contains all descendants, so the dropdown will show this client
        this.loadClients();
      } else {
        // Navigate to client details for non-multilevel clients
        this.navigateToClientDetails(item.id);
      }
    },

    // Go back to parent level in the hierarchy
    goBack() {
      if (this.navigationHistory.length > 0) {
        // Pop the previous parent from history
        const previousParentId = this.navigationHistory.pop();
        this.filters.parentClientId = previousParentId;
        this.filters.includeSubclients = false;
        this.loadClients();
      } else {
        // Go to root (null parent = show direct subclients of current user's client)
        this.filters.parentClientId = null;
        this.filters.includeSubclients = false;
        this.loadClients();
      }
    },

    // Check if client is active
    isActiveClient(item) {
      return item.status_name?.toLowerCase() === 'active';
    },

    // Check if client is blocked
    isBlockedClient(item) {
      return item.status_name?.toLowerCase() === 'blocked';
    },

    // Check if client is suspended
    isSuspendedClient(item) {
      return item.status_name?.toLowerCase() === 'suspended';
    },

    // Open client action dialog
    openClientActionDialog(item, actionType) {
      this.clientActionItem = item;
      this.clientActionType = actionType;
      this.deleteReason = '';
      this.clientActionDialog = true;
    },

    // Execute client action (call API)
    async executeClientAction() {
      if (!this.clientActionItem || !this.clientActionType) return;

      this.clientActionLoading = true;
      try {
        let response;
        const clientId = this.clientActionItem.id;

        switch (this.clientActionType) {
          case 'limit':
            response = await limitClient(this.token, clientId);
            break;
          case 'suspend':
            response = await suspendClient(this.token, clientId);
            break;
          case 'delete':
            response = await deleteClient(this.token, clientId, this.deleteReason);
            break;
          case 'unblock':
            response = await unblockClient(this.token, clientId);
            break;
          case 'block':
            response = await blockClient(this.token, clientId);
            break;
          case 'reactivate':
            response = await reactivateClient(this.token, clientId);
            break;
          default:
            throw new Error('Unknown action type');
        }

        if (response.error) {
          this.snackbarMessage = response.message || 'Eroare la executarea actiunii';
          this.snackbarColor = 'error';
          this.snackbar = true;
        } else {
          this.clientActionDialog = false;
          this.snackbarMessage = response.message || 'Actiune executata cu succes';
          this.snackbarColor = 'success';
          this.snackbar = true;
          this.loadClients();
        }
      } catch (err) {
        console.error("Error executing client action:", err);
        this.snackbarMessage = 'Eroare la executarea actiunii';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.clientActionLoading = false;
      }
    },

    // Impersonation methods
    canManageDistributorFor(item) {
      const roles = store.getters.getRoles || [];
      const isRoot = roles.includes('root');
      const isAdminDistributor = roles.includes('admin_distributor');
      const currentUserClientId = this.user?.client_id || this.user?.client?.id;
      const isDefaultAdminL0ForOwnClient = roles.includes('default_admin_l0') && currentUserClientId === item.id;
      return isRoot || isAdminDistributor || isDefaultAdminL0ForOwnClient;
    },

    canImpersonate(item) {
      // Check if user has required role
      const roles = store.getters.getRoles || [];
      const allowedRoles = ['root', 'admin', 'manage_subclients', 'standard_admin_l0', 'default_admin_l0', 'default_admin_l1'];
      const hasRole = allowedRoles.some(role => roles.includes(role));

      // Cannot impersonate own client
      const currentUserClientId = this.user?.client_id || this.user?.client?.id;
      const isNotOwnClient = currentUserClientId !== item.id;

      // Must have role and not be own client
      return hasRole && isNotOwnClient;
    },

    async openImpersonationDialog(item) {
      this.impersonationItem = item;
      this.loadingImpersonationUsers = true;
      try {
        const result = await getClientUsersForImpersonation(this.token, item.id);
        if (result.error) {
          alert(result.message || 'Eroare la incarcarea userilor');
          return;
        }
        this.impersonationUsers = result.users || [];
        this.impersonationClientName = result.client_name || item.name;
        this.showImpersonationDialog = true;
      } catch (err) {
        log("Error loading impersonation users:", err);
        alert('Eroare la incarcarea userilor');
      } finally {
        this.loadingImpersonationUsers = false;
      }
    },

    async impersonateUser(user) {
      this.generatingToken = true;
      try {
        const result = await generateImpersonationToken(this.token, user.id);
        if (result.error) {
          alert(result.message || 'Eroare la generarea tokenului');
          return;
        }
        // Open new tab with impersonation URL
        openImpersonationTab(result.token);
        this.showImpersonationDialog = false;
      } catch (err) {
        log("Error generating impersonation token:", err);
        alert('Eroare la generarea tokenului');
      } finally {
        this.generatingToken = false;
      }
    },
  },

  watch: {
    'filters.parentClientId': {
      handler() {
        if (this.clientsLoaded) {
          this.loadClients();
        }
      }
    },
    'filters.includeSubclients': {
      handler() {
        if (this.clientsLoaded) {
          this.loadClients();
        }
      }
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    // Get the current parent client info for breadcrumb display
    currentParentClient() {
      if (!this.filters.parentClientId) return null;
      // Find in hierarchy data
      const client = this.clientsHierarchyData.find(c => c.id === this.filters.parentClientId);
      return client || { id: this.filters.parentClientId, name: `Client #${this.filters.parentClientId}` };
    },
    clientActionTitle() {
      const titles = {
        'limit': this.t.clients.limit + ' Client',
        'suspend': this.t.clients.suspend + ' Client',
        'delete': this.t.common.delete + ' Client',
        'unblock': this.t.clients.unblock + ' Client',
        'block': this.t.clients.block + ' Client',
        'reactivate': this.t.clients.reactivate + ' Client'
      };
      return titles[this.clientActionType] || this.t.common.actions;
    },

    clientActionTitleClass() {
      const classes = {
        'limit': 'bg-warning',
        'suspend': 'bg-orange text-white',
        'delete': 'bg-error text-white',
        'unblock': 'bg-success text-white',
        'block': 'bg-black text-white',
        'reactivate': 'bg-success text-white'
      };
      return classes[this.clientActionType] || '';
    },

    clientActionMessage() {
      return this.t.messages.confirm_action;
    },

    clientActionButtonColor() {
      const colors = {
        'limit': 'warning',
        'suspend': 'orange',
        'delete': 'error',
        'unblock': 'success',
        'block': 'black',
        'reactivate': 'success'
      };
      return colors[this.clientActionType] || 'primary';
    },

    clientActionButtonText() {
      const texts = {
        'limit': this.t.clients.limit,
        'suspend': this.t.clients.suspend,
        'delete': this.t.common.delete,
        'unblock': this.t.clients.unblock,
        'block': this.t.clients.block,
        'reactivate': this.t.clients.reactivate
      };
      return texts[this.clientActionType] || this.t.common.confirm;
    }
  },

  created() {
    const { getLoggedIn, user, loggedIn, token } = useGetLoggedIn()
    getLoggedIn().then(() => {
      log("dupa created in ClientsView avem:");
      log("user:");
      log(user);
      log("loggedIn: " + loggedIn.value);
      log("token: " + token.value);
      this.token = store.getters.getToken;
      this.client_id = this.$route.params.id;
      this.user = user.value;

      // Check for search or client_id query parameter and set as search filter
      const searchQuery = this.$route.query.search;
      const clientIdQuery = this.$route.query.client_id;
      const parentClientIdQuery = this.$route.query.parent_client_id;
      const includeSubclientsQuery = this.$route.query.include_subclients;

      if (searchQuery) {
        this.filters.searchTerm = searchQuery.toString();
      }

      // Set client_id filter for direct client lookup
      if (clientIdQuery) {
        this.filters.clientId = parseInt(clientIdQuery);
        this.filters.includeSubclients = true;
      } else if (parentClientIdQuery) {
        this.filters.parentClientId = parseInt(parentClientIdQuery);
      }

      // Set include subclients filter from query params
      if (includeSubclientsQuery !== undefined) {
        this.filters.includeSubclients = includeSubclientsQuery === 'true';
      }

      // Load clients on page load
      this.loadClients();
    })
  }
});
</script>

<style scoped>
.ancestor-path {
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.75rem;
  line-height: 1.2;
}
.clickable-name {
  cursor: pointer;
}
.clickable-name:hover {
  background-color: rgba(25, 118, 210, 0.08);
  border-radius: 4px;
}
</style>
